import FVector from '@data/geometry/FVector';
import * as THREE from 'three';
import { SensorData } from 'Simulations/SensorPoint';
import { computeAverageDirectionFromSensors } from '../SensorDirection';

/**
 * Base class for all facade types.
 * Each facade segment is built between two base points.
 */
export class Facade {
    // Core properties:
    protected sensors: SensorData[];
    protected direction: FVector;

    // Geometric properties:
    protected p1: FVector;
    protected p2: FVector;
    protected height: number;

    // Materials:
    protected material: THREE.Material;
    protected static edgeMaterial = new THREE.LineBasicMaterial({ color: 0x000000 });

    // Mesh:
    private group: THREE.Group | null | undefined;

    constructor(p1: FVector, p2: FVector, height: number, material: THREE.Material, sensors: SensorData[] = [], direction?: FVector) {
        this.sensors = sensors ?? [];
        this.p1 = p1;
        this.p2 = p2;
        this.height = height;
        this.material = material;
        this.direction = this.resolveDirection(direction);
    }

    /**
     * Build and return facade geometry.
     */
    build(): THREE.Group {
        return this.buildFacadeQuad(this.p1, this.p2, this.height, this.material, Facade.edgeMaterial);
    }

    protected buildFacadeQuad(
        p1: FVector,
        p2: FVector,
        height: number,
        material: THREE.Material,
        edgeMaterial: THREE.Material
    ): THREE.Group {
        // bottom -> top vertices
        const bottom1 = new FVector(p1.x, p1.y, p1.z);
        const bottom2 = new FVector(p2.x, p2.y, p2.z);
        const top1 = new FVector(p1.x, p1.y, p1.z + height);
        const top2 = new FVector(p2.x, p2.y, p2.z + height);

        const basePoints = [top1, bottom1, top2, bottom2];

        const geom = new THREE.BufferGeometry().setFromPoints(basePoints.map((p) => new THREE.Vector3(p.x, p.y, p.z)));
        geom.setIndex([0, 1, 2, 2, 1, 3]);
        geom.computeVertexNormals();

        const mesh = new THREE.Mesh(geom, material);
        const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geom, 0.01), edgeMaterial);

        const group = new THREE.Group();
        group.add(mesh, edges);
        return group;
    }

    // =====================================
    // UTILITIES
    // =====================================

    /**
     * Resolves floor-level direction only for FLOOR_AVERAGE.
     * If still undefined, each facade edge will resolve its own direction.
     */
    private resolveDirection(direction: FVector | undefined): FVector {
        if (direction) return direction.clone();
        return computeAverageDirectionFromSensors(this.sensors);
    }

    // =====================================
    // GETTERS
    // =====================================

    /**
     * Uses the propagated direction when available, otherwise falls back to the edge sensors.
     */
    protected getFacadeDirection(): FVector | undefined {
        if (this.direction && !FVector.isZero(this.direction)) return this.direction;
        return computeAverageDirectionFromSensors(this.sensors);
    }

    /**
     * Lazily builds and caches the facade group.
     */
    getGroup(): THREE.Group | null {
        if (this.group === undefined) {
            this.group = this.build();
        }
        return this.group;
    }
}
